import { useEffect, useId, useRef, useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { forgeName } from '@/features/identity';
import type { ForgeAppView } from '@/lib/api/generated/model';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';
import { CopyButton } from '@/shared/components/CopyButton';
import { ExternalLink } from '@/shared/components/ExternalLink';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { RetryButton } from '@/shared/components/RetryButton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { appSetupSteps, appStatusWords } from '../forgeApps';
import {
  ForgeAppSecretRefused,
  isForgeAppFormError,
  useForgeApps,
  useRemoveForgeApp,
  useSetForgeApp,
} from '../hooks/useForgeApps';
import {
  FORGE_APPS_COPY as COPY,
  LINKED_ACCOUNTS_COPY,
} from '../linkedAccountsCopy';

/**
 * Each forge host's sign-in app (spec 161 US1): what lets people link their
 * account by signing in instead of pasting a token. One form is open at a
 * time, inline, so the redirect address it asks for stays in view.
 *
 * Since spec 165 it lives in a sheet, which names it: the heading is then
 * for screen readers only. `initialHost` opens that host's form, for "Set up"
 * in Needs a look.
 */
export function ForgeAppsSection({
  initialHost = null,
  inSheet = false,
}: {
  initialHost?: string | null;
  inSheet?: boolean;
} = {}) {
  const apps = useForgeApps();
  const remove = useRemoveForgeApp();
  const [openHost, setOpenHost] = useState<string | null>(initialHost);
  const [removing, setRemoving] = useState<ForgeAppView | null>(null);
  const showLoading = usePendingDisplay(apps.isPending);
  const data = apps.data;

  return (
    <section
      // In the sheet, its title names it; on a page, its own heading does.
      aria-labelledby={inSheet ? undefined : 'linked-accounts-sign-in'}
      aria-label={inSheet ? COPY.heading : undefined}
      className="flex flex-col gap-3"
    >
      {inSheet ? null : (
        <div className="flex flex-col gap-1">
          <h2 id="linked-accounts-sign-in">
            <EyebrowLabel>{COPY.heading}</EyebrowLabel>
          </h2>
          <p className="text-muted-foreground max-w-2xl text-sm">
            {COPY.intro}
          </p>
        </div>
      )}

      {apps.isPending ? (
        showLoading ? (
          <p className="text-muted-foreground text-sm">
            {LINKED_ACCOUNTS_COPY.loading}
          </p>
        ) : null
      ) : apps.isError || !data ? (
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-sm">{COPY.loadFailed}</p>
          <RetryButton
            onRetry={() => void apps.refetch()}
            isFetching={apps.isFetching}
          />
        </div>
      ) : !data.available ? (
        <p className="text-muted-foreground max-w-2xl text-sm">
          {COPY.unavailable}
        </p>
      ) : (
        <>
          {data.redirect_uri ? (
            <div className="flex max-w-2xl flex-col gap-1">
              <span className="text-muted-foreground text-xs">
                {COPY.redirectLabel}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <code className="bg-muted text-foreground min-w-0 rounded px-2 py-1 font-mono text-xs break-all">
                  {data.redirect_uri}
                </code>
                <CopyButton
                  text={data.redirect_uri}
                  label={COPY.copyRedirect}
                  copiedLabel={COPY.copied}
                />
              </div>
            </div>
          ) : null}

          {data.items.length === 0 ? (
            <p className="text-muted-foreground text-sm">{COPY.noHosts}</p>
          ) : (
            <ul className="border-border divide-border flex max-w-2xl flex-col divide-y rounded-md border">
              {data.items.map((app) => (
                <AppRow
                  key={app.host}
                  app={app}
                  open={openHost === app.host}
                  onOpen={() => setOpenHost(app.host)}
                  onClose={() => setOpenHost(null)}
                  onRemove={() => setRemoving(app)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <ConfirmDestructiveDialog
        open={removing !== null}
        title={removing ? COPY.removeTitle(removing.host) : ''}
        confirmLabel={COPY.remove}
        pendingLabel={COPY.removePending}
        isPending={remove.isPending}
        onConfirm={() => {
          if (!removing) return;
          remove.mutate(
            { host: removing.host },
            { onSettled: () => setRemoving(null) },
          );
        }}
        onCancel={() => setRemoving(null)}
      >
        {removing ? COPY.removeBody(removing.host) : null}
      </ConfirmDestructiveDialog>
    </section>
  );
}

function AppRow({
  app,
  open,
  onOpen,
  onClose,
  onRemove,
}: {
  app: ForgeAppView;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="flex flex-col gap-3 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-col">
          <p className="text-foreground text-sm">
            <span className="font-medium">{app.host}</span>
            <span className="text-muted-foreground">
              {' '}
              · {forgeName(app.provider)}
            </span>
          </p>
          <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
            <span>{appStatusWords(app)}</span>
            {app.configured && app.client_id ? (
              <span className="break-all">{COPY.clientId(app.client_id)}</span>
            ) : null}
            {app.configured && app.changed_at ? (
              <span>{COPY.changed(app.changed_at.slice(0, 10))}</span>
            ) : null}
          </p>
        </div>
        {!open ? (
          <span className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              aria-label={
                app.configured
                  ? COPY.changeLabel(app.host)
                  : COPY.setUpLabel(app.host)
              }
              onClick={onOpen}
            >
              {app.configured ? COPY.change : COPY.setUpAction}
            </Button>
            {app.configured ? (
              <Button
                size="sm"
                variant="ghost"
                aria-label={COPY.removeLabel(app.host)}
                onClick={onRemove}
              >
                {COPY.remove}
              </Button>
            ) : null}
          </span>
        ) : null}
      </div>
      {open ? <ForgeAppForm app={app} onDone={onClose} /> : null}
    </li>
  );
}

/**
 * The app's client ID and secret. The secret lives in this component's state
 * only, is never shown back, and is gone when the form closes; left empty, the
 * stored one is kept.
 */
function ForgeAppForm({
  app,
  onDone,
}: {
  app: ForgeAppView;
  onDone: () => void;
}) {
  const save = useSetForgeApp();
  const setup = appSetupSteps(app.provider, app.host);
  const idId = useId();
  const secretId = useId();
  const hintId = useId();
  const errorId = useId();
  const secretInput = useRef<HTMLInputElement>(null);
  const [clientId, setClientId] = useState(app.client_id ?? '');
  const [secret, setSecret] = useState('');
  const [shown, setShown] = useState(false);

  const error = isForgeAppFormError(save.error) ? save.error : null;
  const secretProblem =
    error instanceof ForgeAppSecretRefused ? error.message : null;
  const formProblem = error && !secretProblem ? error.message : null;

  useEffect(() => {
    if (secretProblem) secretInput.current?.focus();
  }, [secretProblem]);

  const close = () => {
    setSecret('');
    save.reset();
    onDone();
  };

  return (
    <form
      noValidate
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (save.isPending || clientId.trim() === '') return;
        save.mutate(
          {
            host: app.host,
            clientId,
            secret,
            existed: app.configured,
          },
          { onSuccess: close },
        );
      }}
    >
      <ol className="text-muted-foreground flex list-decimal flex-col gap-1 pl-5 text-sm">
        {setup.steps.map((step, index) => (
          <li key={step}>
            {step}
            {index === 0 ? (
              <>
                {' '}
                <ExternalLink href={setup.createUrl}>
                  {COPY.openForge(app.host)}
                </ExternalLink>
              </>
            ) : null}
          </li>
        ))}
      </ol>

      <div className="flex flex-col gap-1">
        <Label htmlFor={idId}>{setup.idLabel}</Label>
        <Input
          id={idId}
          autoComplete="off"
          spellCheck={false}
          value={clientId}
          onChange={(event) => setClientId(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={secretId}>{setup.secretLabel}</Label>
        <div className="flex items-center gap-2">
          <Input
            ref={secretInput}
            id={secretId}
            type={shown ? 'text' : 'password'}
            autoComplete="off"
            spellCheck={false}
            value={secret}
            onChange={(event) => {
              setSecret(event.target.value);
              if (secretProblem) save.reset();
            }}
            aria-invalid={secretProblem !== null}
            aria-describedby={
              [app.configured ? hintId : null, secretProblem ? errorId : null]
                .filter(Boolean)
                .join(' ') || undefined
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={shown ? COPY.hideSecret : COPY.showSecret}
            aria-pressed={shown}
            onClick={() => setShown((value) => !value)}
          >
            {shown ? (
              <EyeOffIcon aria-hidden="true" />
            ) : (
              <EyeIcon aria-hidden="true" />
            )}
          </Button>
        </div>
        {app.configured ? (
          <p id={hintId} className="text-muted-foreground text-xs">
            {COPY.keepSecret}
          </p>
        ) : null}
        {secretProblem ? (
          <p id={errorId} role="alert" className="text-destructive text-sm">
            {secretProblem}
          </p>
        ) : null}
      </div>

      {formProblem ? (
        <p role="alert" className="text-destructive text-sm">
          {formProblem}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          disabled={save.isPending || clientId.trim() === ''}
        >
          {save.isPending ? COPY.saving : COPY.save}
        </Button>
        <Button type="button" variant="link" onClick={close}>
          {COPY.cancel}
        </Button>
      </div>
    </form>
  );
}
